<template>
  <div class="space-y-8">
    <!-- Header Section -->
    <div class="flex justify-between items-center">
      <div>
        <h3 class="text-lg font-medium text-heading">
          {{ $t('AI_SETTINGS.OVERVIEW.TITLE') }}
        </h3>
        <p class="text-sm text-body-light">
          {{ $t('AI_SETTINGS.OVERVIEW.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-4">
        <div class="text-sm text-body-light">
          {{ $t('AI_SETTINGS.OVERVIEW.BOT_VERSION') }}
        </div>
        <woot-button
          color-scheme="primary"
          :loading="isSaving"
          @click="saveBotConfiguration"
        >
          {{ $t('AI_SETTINGS.OVERVIEW.SAVE_CHANGES') }}
        </woot-button>
      </div>
    </div>

    <!-- Basic Information Section -->
    <div class="bg-background rounded-lg border border-border p-6">
      <h4 class="text-md font-medium text-heading mb-4">
        {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.TITLE') }}
      </h4>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Bot Name -->
        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BOT_NAME') }}
          </label>
          <input
            v-model="botConfig.bot_name"
            type="text"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BOT_NAME_PLACEHOLDER')"
          />
        </div>

        <!-- Business Name -->
        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BUSINESS_NAME') }}
          </label>
          <input
            v-model="botConfig.business_name"
            type="text"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BUSINESS_NAME_PLACEHOLDER')"
          />
        </div>

        <!-- Response Language -->
        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE') }}
          </label>
          <select
            v-model="botConfig.language"
            class="form-input"
          >
            <option value="auto">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.auto') }}</option>
            <option value="en">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.en') }}</option>
            <option value="vi">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.vi') }}</option>
          </select>
        </div>  
      </div>
    </div>

    <!-- AI Configuration and Behavior Section -->
    <div class="bg-background rounded-lg border border-border p-6">
      <h4 class="text-md font-medium text-heading mb-4">
        {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TITLE') }}
      </h4>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Response Speed -->
        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.RESPONSE_SPEED') }}
          </label>
          <input
            v-model.number="botConfig.response_speed"
            type="number"
            min="0"
            max="60"
            class="form-input"
          />
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.RESPONSE_SPEED_DESCRIPTION') }}
          </p>
        </div>

        <!-- Temperature -->
        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TEMPERATURE') }}
          </label>
          <input
            v-model.number="botConfig.temperature"
            type="number"
            min="0"
            max="2"
            step="0.1"
            class="form-input"
          />
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TEMPERATURE_DESCRIPTION') }}
          </p>
        </div>

        <!-- Bot Modes Switches -->
        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.PRODUCT_CONSULTING') }}
          </label>
          <woot-switch :model-value="botConfig.product_consulting_enabled" @input="val => botConfig.product_consulting_enabled = val" />
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.PRODUCT_CONSULTING_DESCRIPTION') }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.CUSTOMER_SUPPORT') }}
          </label>
          <woot-switch :model-value="botConfig.customer_support_enabled" @input="val => botConfig.customer_support_enabled = val" />
          <p class="text-xs text-body-light mt-1">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.CUSTOMER_SUPPORT_DESCRIPTION') }}
          </p>
        </div>

        <!-- Addressing Style -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE') }}
          </label>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs text-body-light mb-1">
                {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_XUNG') }}
              </label>
              <input
                v-model="botConfig.addressing_style.xung"
                type="text"
                class="form-input"
                :placeholder="$t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_PLACEHOLDER')"
              />
            </div>
            <div>
              <label class="block text-xs text-body-light mb-1">
                {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_HO') }}
              </label>
              <input
                v-model="botConfig.addressing_style.ho"
                type="text"
                class="form-input"
                :placeholder="$t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_PLACEHOLDER')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Situation Handling Section -->
    <div class="bg-background rounded-lg border border-border p-6">
      <h4 class="text-md font-medium text-heading mb-4">
        {{ $t('AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.TITLE') }}
      </h4>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Unknown Question Action -->
        <div>
          <label class="block text-sm font-medium text-heading mb-2">
            {{ $t('AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.UNKNOWN_QUESTION_ACTION') }}
          </label>
          <select
            v-model="botConfig.unknown_question_action"
            class="form-input"
          >
            <option value="auto_reply">{{ $t('AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.UNKNOWN_QUESTION_OPTIONS.auto_reply') }}</option>
            <option value="no_reply">{{ $t('AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.UNKNOWN_QUESTION_OPTIONS.no_reply') }}</option>
          </select>
        </div>

        <!-- Bot Silent Toggle -->
        <div>
          <label class="flex items-center">
            <input
              v-model="botConfig.bot_silent"
              type="checkbox"
              class="rounded border-border text-woot-600 focus:ring-woot-500"
            />
            <span class="ml-2 text-sm font-medium text-heading">
              {{ $t('AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.BOT_SILENT') }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables'

export default {
  name: 'OverviewTab',
  
  props: {
    aiConfiguration: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['refresh'],
  
  data() {
    return {
      isSaving: false,
      botConfig: {
        bot_name: '',
        business_name: '',
        language: 'auto',
        response_speed: 5,
        temperature: 0.6,
        addressing_style: { xung: 'mình', ho: 'bạn' },
        unknown_question_action: 'auto_reply',
        bot_silent: false,
        product_consulting_enabled: false,
        customer_support_enabled: false,
      }
    }
  },
  
  watch: {
    aiConfiguration: {
      handler(newConfig) {
        if (newConfig) {
          this.loadBotConfiguration(newConfig)
        }
      },
      immediate: true
    }
  },
  
  methods: {
    loadBotConfiguration(config) {
      if (!config) return
      
      this.botConfig = {
        bot_name: config.bot_name || '',
        business_name: config.business_name || '',
        language: config.language || 'auto',
        response_speed: config.response_speed || 5,
        temperature: config.temperature || 0.6,
        addressing_style: config.addressing_style || { xung: 'mình', ho: 'bạn' },
        unknown_question_action: config.unknown_question_action || 'auto_reply',
        bot_silent: config.bot_silent || false,
        product_consulting_enabled: !!config.product_consulting_enabled,
        customer_support_enabled: !!config.customer_support_enabled,
      }
    },
    
    async saveBotConfiguration() {
      if (!this.aiConfiguration) {
        useAlert(this.$t('AI_SETTINGS.OVERVIEW.ERRORS.NO_CONFIG'), 'error')
        return
      }
      
      try {
        this.isSaving = true
        const accountId = this.$store.getters.getCurrentAccountId
        
        await window.axios.put(
          `/api/v1/accounts/${accountId}/ai_configurations/${this.aiConfiguration.id}`,
          { ai_configuration: this.botConfig }
        )
        
        useAlert(this.$t('AI_SETTINGS.OVERVIEW.SUCCESS.CONFIG_SAVED'), 'success')
        this.$emit('refresh')
      } catch (error) {
        console.error('Error saving bot configuration:', error)
        useAlert(this.$t('AI_SETTINGS.OVERVIEW.ERRORS.SAVE_FAILED'), 'error')
      } finally {
        this.isSaving = false
      }
    },
  }
}
</script>
